import React, { useState } from 'react';
import {
  CloudArrowUpIcon,
  CogIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { TRANSACTION_STATUS } from '../../hooks/useTransactions';
import ExportConfigModal from '../upload/ExportConfigModal';

// Data Quality Indicator Component
const DataQualityIndicator = ({ dataQuality }) => {
  const { isComplete, qualityScore, unknownDates, unknownDescriptions, unknownAmounts, totalTransactions } = dataQuality;

  // Determine quality level and styling
  const getQualityConfig = () => {
    if (isComplete) {
      return {
        icon: ShieldCheckIcon,
        text: 'All data complete - Ready for accounting software',
        textColor: 'text-green-700',
        bgColor: 'bg-green-100',
        iconColor: 'text-green-600'
      };
    } else if (qualityScore >= 90) {
      const issueCount = unknownDates + unknownDescriptions + unknownAmounts;
      return {
        icon: ExclamationTriangleIcon,
        text: `${issueCount} transaction${issueCount !== 1 ? 's' : ''} need review - Click for details`,
        textColor: 'text-yellow-700',
        bgColor: 'bg-yellow-100',
        iconColor: 'text-yellow-600',
        clickable: true
      };
    } else {
      return {
        icon: ExclamationTriangleIcon,
        text: 'Major data issues detected - Review before importing',
        textColor: 'text-red-700',
        bgColor: 'bg-red-100',
        iconColor: 'text-red-600',
        clickable: true
      };
    }
  };

  const config = getQualityConfig();
  const QualityIcon = config.icon;

  const [showDetails, setShowDetails] = useState(false);

  const handleClick = () => {
    if (config.clickable) {
      setShowDetails(true);
    }
  };

  return (
    <>
      <div className="mt-2">
        <div
          className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${config.bgColor} ${config.textColor} ${config.clickable ? 'cursor-pointer hover:opacity-80' : ''}`}
          onClick={handleClick}
        >
          <QualityIcon className={`h-3 w-3 mr-1 ${config.iconColor}`} />
          <span>{config.text}</span>
        </div>
      </div>

      {/* Quality Details Modal */}
      {showDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl p-6 m-4 max-w-md w-full">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Data Quality Details
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm font-medium text-gray-900 mb-2">Summary</div>
                <div className="text-sm text-gray-600">
                  <div>Total transactions: {totalTransactions}</div>
                  <div>Quality score: {qualityScore}%</div>
                </div>
              </div>

              {(unknownDates > 0 || unknownDescriptions > 0 || unknownAmounts > 0) && (
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <div className="text-sm font-medium text-yellow-900 mb-2">Issues Found</div>
                  <div className="text-sm text-yellow-800 space-y-1">
                    {unknownDates > 0 && <div>• {unknownDates} transactions with unknown dates</div>}
                    {unknownDescriptions > 0 && <div>• {unknownDescriptions} transactions with unknown descriptions</div>}
                    {unknownAmounts > 0 && <div>• {unknownAmounts} transactions with unknown amounts</div>}
                  </div>
                </div>
              )}

              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-sm font-medium text-blue-900 mb-2">Recommendation</div>
                <div className="text-sm text-blue-800">
                  Review "Unknown" entries in your CSV file before importing to your accounting software.
                  You may need to manually correct these entries for accurate bookkeeping.
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowDetails(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const TransactionStatus = ({ transaction, onDownload, onDownloadRaw, onRetry, onDelete }) => {
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState(null);

  // Status configuration
  const statusConfig = {
    [TRANSACTION_STATUS.UPLOADING]: {
      icon: CloudArrowUpIcon,
      label: 'Uploading...',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      showProgress: true
    },
    [TRANSACTION_STATUS.PROCESSING]: {
      icon: CogIcon,
      label: 'Converting to CSV...',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      showProgress: true,
      animate: true
    },
    [TRANSACTION_STATUS.COMPLETED]: {
      icon: CheckCircleIcon,
      label: 'Ready for Download',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      showDownload: true
    },
    [TRANSACTION_STATUS.ERROR]: {
      icon: ExclamationCircleIcon,
      label: 'Conversion Failed',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      showRetry: true
    },
    [TRANSACTION_STATUS.DOWNLOADING]: {
      icon: ArrowDownTrayIcon,
      label: 'Downloading...',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      showProgress: true
    }
  };

  const config = statusConfig[transaction.status] || statusConfig[TRANSACTION_STATUS.ERROR];
  const StatusIcon = config.icon;

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format timestamp
  const formatTimestamp = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Handle export button clicks
  const handleExportClick = (format) => {
    setExportFormat(format);
    setShowExportModal(true);
  };

  return (
    <div className={`relative p-4 rounded-lg border ${config.bgColor} ${config.borderColor} transition-all duration-200`}>
      {/* Delete button - top right corner */}
      {onDelete && (
        <button
          onClick={() => onDelete(transaction)}
          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200 z-10"
          title="Delete this conversion"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
      
      {/* Responsive layout: stack on mobile, side-by-side on larger screens */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between space-y-3 lg:space-y-0 pr-8">
        {/* Left side - Status and file info */}
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          {/* Status Icon */}
          <div className={`flex-shrink-0 ${config.color}`}>
            <StatusIcon 
              className={`h-6 w-6 ${config.animate ? 'animate-spin' : ''}`}
            />
          </div>

          {/* File Details */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-1">
              <h3 className="text-sm font-medium text-gray-900 break-words sm:truncate max-w-full">
                {transaction.fileName || 'Unknown file'}
              </h3>
              <span className={`text-xs font-medium ${config.color} flex-shrink-0 mt-1 sm:mt-0`}>
                {config.label}
              </span>
            </div>
            
            <div className="text-xs text-gray-500 space-y-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
                <span className="flex-shrink-0">Size: {formatFileSize(transaction.fileSize)}</span>
                <span className="flex-shrink-0">Credits: {transaction.creditsDeducted || 0}</span>
              </div>
              <div>
                {formatTimestamp(transaction.createdAt)}
              </div>
            </div>

            {/* Data Quality Indicator - only show for completed transactions */}
            {transaction.status === TRANSACTION_STATUS.COMPLETED && transaction.dataQuality && (
              <DataQualityIndicator dataQuality={transaction.dataQuality} />
            )}

            {/* Progress Bar for processing states */}
            {config.showProgress && (
              <div className="mt-2">
                <div className="bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      transaction.status === TRANSACTION_STATUS.UPLOADING ? 'bg-blue-500' :
                      transaction.status === TRANSACTION_STATUS.PROCESSING ? 'bg-yellow-500' :
                      'bg-blue-500'
                    }`}
                    style={{
                      width: transaction.progress ? `${transaction.progress}%` :
                             transaction.status === TRANSACTION_STATUS.PROCESSING ? '60%' : '100%'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Error message */}
            {transaction.status === TRANSACTION_STATUS.ERROR && transaction.errorMessage && (
              <div className="mt-2 text-xs text-red-600 bg-red-100 p-2 rounded">
                {transaction.errorMessage}
                {transaction.processingTimedOut && (
                  <div className="mt-1 text-xs text-red-500">
                    💡 Tip: Large or complex PDFs may need multiple attempts
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right side - Action buttons (responsive positioning) */}
        <div className="flex-shrink-0 lg:ml-4">
          {config.showDownload && onDownload && (
            <div className="flex flex-col space-y-2 min-w-[140px]">
              {/* CSV Download Button */}
              <button
                onClick={() => onDownload(transaction)}
                className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors duration-200 whitespace-nowrap"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="truncate">Download CSV</span>
              </button>

              {/* OFX Export Button */}
              <button
                onClick={() => handleExportClick('OFX')}
                className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors duration-200 whitespace-nowrap"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="truncate">Download OFX</span>
              </button>

              {/* QBO Export Button */}
              <button
                onClick={() => handleExportClick('QBO')}
                className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors duration-200 whitespace-nowrap"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="truncate">Download QBO</span>
              </button>
            </div>
          )}

          {config.showRetry && onRetry && (
            <button
              onClick={() => onRetry(transaction)}
              className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-300 hover:bg-red-50 rounded-md transition-colors duration-200 whitespace-nowrap"
            >
              Try Again
            </button>
          )}
        </div>
      </div>

      {/* Export Config Modal */}
      {showExportModal && exportFormat && (
        <ExportConfigModal
          isOpen={showExportModal}
          onClose={() => {
            setShowExportModal(false);
            setExportFormat(null);
          }}
          format={exportFormat}
          transactionId={transaction.id}
          fileName={transaction.fileName}
        />
      )}
    </div>
  );
};

export default TransactionStatus;